import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CloseIcon } from '../../../../assets/icons';
import { DateRangePicker, Range, createStaticRanges } from 'react-date-range';
import styles from './FilterV2.module.scss';
import { FilterV2Category, FilterV2Props } from './filter-v2.types';

const FilterV2: React.FC<FilterV2Props> = ({
  categories,
  onApplyFilter,
  onResetFilter,
  onClose,
  isOpen,
}) => {
  const [activeCategoryId, setActiveCategoryId] = useState<string>(categories[0]?.id ?? '');
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  // Reset to first category whenever filter opens
  useEffect(() => {
    if (isOpen) setActiveCategoryId(categories[0]?.id ?? '');
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen) return null;

  const activeCategory = categories.find(c => c.id === activeCategoryId);

  const activeChips: { key: string; label: string; onRemove: () => void }[] = [];
  for (const category of categories) {
    if (category.type === 'checkbox') {
      category.options.filter(o => o.isSelected).forEach(opt => {
        activeChips.push({
          key: `${category.id}-${opt.value}`,
          label: `${category.label}: ${opt.label}`,
          onRemove: () => category.onChange(
            category.options.filter(o => o.isSelected && o.value !== opt.value).map(o => o.value)
          ),
        });
      });
    } else if (category.type === 'radio') {
      const sel = category.options.find(o => o.isSelected);
      if (sel) {
        activeChips.push({
          key: `${category.id}-${sel.value}`,
          label: `${category.label}: ${sel.label}`,
          onRemove: () => category.onChange(''),
        });
      }
    } else if (category.type === 'date') {
      const { startDate, endDate } = category.initialDateRange ?? {};
      if (startDate && endDate) {
        const fmt = (d: Date) => d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
        activeChips.push({
          key: category.id,
          label: `${category.label}: ${fmt(startDate)} → ${fmt(endDate)}`,
          onRemove: () => category.onClear?.(),
        });
      }
    } else if (category.type === 'searchable' || category.type === 'paginated') {
      if (category.selectedId) {
        const opt = category.options.find(o => o.id === category.selectedId);
        if (opt) {
          activeChips.push({
            key: `${category.id}-${category.selectedId}`,
            label: `${category.label}: ${opt.label}`,
            onRemove: () => category.onChange(''),
          });
        }
      }
    }
  }

  return (
    <>
      <div className={styles.backdrop} onClick={onClose} />
      <div className={styles.container} ref={containerRef}>
      <div className={`${styles.body} ${activeCategory?.type === 'date' ? styles.bodyExpanded : ''}`}>
        <div className={styles.sidebar}>
          {categories.map(category => (
            <div
              key={category.id}
              className={`${styles.sidebarItem} ${activeCategoryId === category.id ? styles.sidebarItemActive : ''}`}
              onClick={() => setActiveCategoryId(category.id)}
            >
              {category.label}
            </div>
          ))}
        </div>

        <div className={styles.rightContainer}>
          <div className={styles.contentCard}>
            {activeCategory && <CategoryContent category={activeCategory} />}
          </div>
        </div>
      </div>

      {activeChips.length > 0 && (
        <div className={styles.activeFiltersSection}>
          <div className={styles.chipsWrapper}>
            {activeChips.map(chip => (
              <div key={chip.key} className={styles.chip}>
                <button className={styles.chipRemove} onClick={chip.onRemove} aria-label="Remove filter">
                  <CloseIcon width={16} height={16} />
                </button>
                <span className={styles.chipLabel}>{chip.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className={styles.footer}>
        {onResetFilter && (
          <button className={styles.resetButton} onClick={onResetFilter}>
            Reset
          </button>
        )}
        <button className={styles.applyButton} onClick={onApplyFilter}>
          Apply filters
        </button>
      </div>
    </div>
    </>
  );
};

const CategoryContent: React.FC<{ category: FilterV2Category }> = ({ category }) => {
  if (category.type === 'date') {
    return <DateContent category={category} />;
  }
  return <ListContent category={category as FilterV2Category & { type: 'checkbox' | 'radio' | 'paginated' | 'searchable' }} />;
};

const dateStaticRanges = createStaticRanges([
  {
    label: 'Today',
    range: () => {
      const d = new Date();
      return { startDate: new Date(d.getFullYear(), d.getMonth(), d.getDate()), endDate: d };
    },
  },
  {
    label: 'This Week',
    range: () => {
      const d = new Date();
      const day = d.getDay();
      const diff = day === 0 ? -6 : 1 - day;
      return { startDate: new Date(d.getFullYear(), d.getMonth(), d.getDate() + diff), endDate: d };
    },
  },
  {
    label: 'This Month',
    range: () => {
      const d = new Date();
      return { startDate: new Date(d.getFullYear(), d.getMonth(), 1), endDate: d };
    },
  },
  {
    label: 'Last Week',
    range: () => {
      const d = new Date();
      const day = d.getDay();
      const diff = day === 0 ? -6 : 1 - day;
      const startThisWeek = new Date(d.getFullYear(), d.getMonth(), d.getDate() + diff);
      const start = new Date(startThisWeek.getFullYear(), startThisWeek.getMonth(), startThisWeek.getDate() - 7);
      return { startDate: start, endDate: new Date(start.getFullYear(), start.getMonth(), start.getDate() + 6) };
    },
  },
  {
    label: 'Last Month',
    range: () => {
      const d = new Date();
      return {
        startDate: new Date(d.getFullYear(), d.getMonth() - 1, 1),
        endDate: new Date(d.getFullYear(), d.getMonth(), 0),
      };
    },
  },
  {
    label: 'Custom',
    range: () => { const d = new Date(); return { startDate: d, endDate: d }; },
    isSelected: () => false,
  },
]);

const DateContent: React.FC<{ category: FilterV2Category & { type: 'date' } }> = ({ category }) => {
  const today = new Date();
  const defaultRange: Range = { startDate: today, endDate: today, key: 'selection' };
  const dateRange = category.initialDateRange ?? defaultRange;

  return (
    <div className={styles.dateContent}>
      <DateRangePicker
        ranges={[dateRange]}
        onChange={category.onChange}
        moveRangeOnFirstSelection={false}
        months={1}
        direction="horizontal"
        maxDate={today}
        showDateDisplay={false}
        rangeColors={['#171717']}
        staticRanges={dateStaticRanges}
        inputRanges={[]}
      />
    </div>
  );
};

const ListContent: React.FC<{ category: FilterV2Category & { type: 'checkbox' | 'radio' | 'paginated' | 'searchable' } }> = ({ category }) => {
  const [searchValue, setSearchValue] = useState('');
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadingRef = useRef(false);

  useEffect(() => {
    if (category.type === 'paginated') {
      loadingRef.current = category.isLoading;
    }
  }, [category]);

  const lastItemCallback = useCallback(
    (node: HTMLDivElement | null) => {
      if (category.type !== 'paginated') return;
      if (loadingRef.current || !category.hasMore) return;
      if (observerRef.current) observerRef.current.disconnect();

      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && category.hasMore && !loadingRef.current) {
            loadingRef.current = true;
            category.onLoadMore();
          }
        },
        { rootMargin: '100px', threshold: 0.1 },
      );

      if (node) observerRef.current.observe(node);
    },
    [category],
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);

    if (category.type === 'paginated' && category.onSearch) {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = setTimeout(() => category.onSearch!(value), 300);
    }
  };

  const getFilteredOptions = () => {
    if (category.type === 'paginated') return category.options;
    return category.options.filter(opt =>
      opt.label.toLowerCase().includes(searchValue.toLowerCase()),
    );
  };

  const isSelected = (option: { id?: string; value?: string }) => {
    if (category.type === 'paginated' || category.type === 'searchable') return category.selectedId === option.id;
    if (category.type === 'radio') {
      const selected = (category.options as { value: string; isSelected?: boolean }[]).find(o => o.isSelected);
      return selected?.value === (option as { value: string }).value;
    }
    return !!(option as { isSelected?: boolean }).isSelected;
  };

  const handleSelect = (option: { id?: string; value?: string }) => {
    if (category.type === 'paginated' || category.type === 'searchable') {
      category.onChange(option.id!);
      return;
    }
    if (category.type === 'radio') {
      category.onChange((option as { value: string }).value);
      return;
    }
    // checkbox: toggle
    const currentSelected = category.options
      .filter(o => o.isSelected)
      .map(o => o.value);
    const value = (option as { value: string }).value;
    const next = currentSelected.includes(value)
      ? currentSelected.filter(v => v !== value)
      : [...currentSelected, value];
    category.onChange(next);
  };

  const filteredOptions = getFilteredOptions();

  return (
    <>
      <div className={styles.searchHeader}>
        <div className={styles.searchInputWrapper}>
          <svg className={styles.searchIcon} width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="8" cy="8" r="5.5" stroke="#171717" strokeWidth="1.5" />
            <path d="M12.5 12.5L16 16" stroke="#171717" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Search..."
            value={searchValue}
            onChange={handleSearchChange}
          />
        </div>
      </div>

      <div className={styles.optionsList}>
        {filteredOptions.length === 0 && (
          <div className={styles.emptyMessage}>
            {category.type === 'paginated' && category.isLoading ? 'Loading...' : 'No options'}
          </div>
        )}
        {filteredOptions.map((option, index) => {
          const selected = isSelected(option);
          const isLast = category.type === 'paginated' && index === filteredOptions.length - 1;
          return (
            <div
              key={option.id}
              className={styles.optionRow}
              ref={isLast ? lastItemCallback : null}
              onClick={() => handleSelect(option)}
            >
              <div className={styles.optionInner}>
                <div className={`${styles.checkbox} ${selected ? styles.checkboxChecked : ''}`}>
                  {selected && (
                    <svg width="10" height="8" viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </div>
                <span className={styles.optionLabel}>{option.label}</span>
              </div>
            </div>
          );
        })}
        {category.type === 'paginated' && category.isLoading && filteredOptions.length > 0 && (
          <div className={styles.loadingMore}>Loading more...</div>
        )}
      </div>
    </>
  );
};

export default FilterV2;
